import { axios } from './config';

/**
 * Fetches the list of video categories.
 * @returns {Promise} Axios response promise.
 */
export const getCategories = () => {
	return axios.get('/video/get-categories');
};

/**
 * Fetches videos for a specific category.
 * @param {string} category - The category of videos to fetch.
 * @returns {Promise} Axios response promise.
 */
export const getVideos = (category) => {
	return axios.get(`/video?category=${category}`);
};

/**
 * Uploads a video using the provided FormData object.
 * @param {FormData} videoDocData - The FormData object containing the video upload data.
 * @param {File} videoDocData.video - The video file to upload.
 * @param {string} videoDocData.fileName - The name of the video file.
 * @param {string} videoDocData.title - The title of the video.
 * @param {string} videoDocData.category - The category of the video.
 * @param {string} [videoDocData.subCategory] - The sub-category of the video (optional).
 * @param {string} [videoDocData.description] - The description of the video (optional).
 * @param {boolean} videoDocData.uploadMuted - Indicates whether to upload a muted version of the video.
 * @returns {Promise} Axios response promise.
 */
export const uploadVideo = (videoDocData) => {
	return axios.post('/video/upload', videoDocData);
};

/**
 * Transcodes a video.
 * @param {Object} videoDetails - The video data to transcode.
 * @param {string} videoDetails.fileName - The name of the video file.
 * @param {string} videoDetails.source - The source URL of the video.
 * @param {string} videoDetails.category - The category of the video.
 * @param {string} [videoDetails.subCategory] - The sub-category of the video (optional).
 * @param {string} videoDetails.path - The path of the video.
 * @param {string} videoDetails.audioVersion - The audio version of the video (e.g., 'muted' or 'unmuted').
 * @returns {Promise} Axios response promise.
 */
export const transcodeVideo = (videoDetails) => {
	return axios.post('/video/transcode', videoDetails);
};

/**
 * Fetches a video by its path and audio version.
 * @param {string} category - The category of the video.
 * @param {string} path - The path of the video.
 * @param {string} audioVersion - The audio version of the video.
 * @returns {Promise} Axios response promise.
 */
export const getVideoByPathAndAudioVersion = (category, path, audioVersion) => {
	return axios.get(
		`/video/get-audio-version-doc?category=${category}&path=${path}&audioVersion=${audioVersion}`
	);
};

/**
 * Fetches a video by its path.
 * @param {string} category - The category of the video.
 * @param {string} path - The path of the video.
 * @returns {Promise} Axios response promise.
 */
export const getVideoByPath = (category, path) => {
	return axios.get(`/video/get-doc?category=${category}&path=${path}`);
};

/**
 * Uploads a muted version of a video.
 * @param {Object} videoDetails - The muted video data to upload.
 * @param {string} videoDetails.fileName - The name of the video file.
 * @param {string} videoDetails.title - The title of the video.
 * @param {string} videoDetails.category - The category of the video.
 * @param {string} [videoDetails.subCategory] - The sub-category of the video (optional).
 * @param {string} [videoDetails.description] - The description of the video (optional).
 * @returns {Promise} Axios response promise.
 */
export const uploadMutedVideoVersion = (videoDetails) => {
	return axios.post('/video/muted-upload', videoDetails);
};
